import { configureStore } from '@reduxjs/toolkit';
import { combineReducers } from 'redux';
import { createEpicMiddleware } from 'redux-observable';
import { ajax } from 'rxjs/ajax';
import { map } from 'rxjs/operators';
import { dialogBridgeMiddleware } from '../middleware/dialogBridgeMiddleware.js';
import { notificationBridgeMiddleware } from '../middleware/notificationBridgeMiddleware.js';
import * as reducers from '../reducers/index.js';
import { restoreRedirectedState } from './redirect.js';
import type { ActionPhase } from './storeUtils.js';
import type {
  DeliveryMethods,
  DuplicateContactCheck,
  ShoppingCartItemForCheckout,
} from '../../common/types/checkout.js';
import type { CommonError } from '../../common/types/errors.js';
import type {
  AuthenticatedUserDataState,
  BillingAccountsState,
  CampaignsState,
  ErrorableState,
  OnlineOrderState,
  PaymentsState,
  PickupPointSearchState,
  PunchoutOrderState,
  ReportsState,
  ResourcesState,
  ShoppingCartState,
  SubscriptionActionsState,
  SupportCasesState,
  VirtualCatalogsState,
} from '../../common/types/states.js';
import type {
  AuxiliaryEsimOrderFailureEnum,
  AuxiliaryEsimOrderPhaseEnum,
} from '../../components/AuxiliaryEsimOrderScene/auxiliaryEsimOrderEnums.js';
import type { DeviceCheckoutDeliveryDetailsType } from '../../components/DeviceCheckoutDeliveryDetails/DeviceCheckoutDeliveryDetailsUtils.js';
import type { AuxiliaryEsimServiceOrderResult, BillingAccount, Subscription } from '../../generated/api/models.js';
import type { SubmitOnlineOrderAction } from '../actions/index.js';
import type { EpicDependencies } from '../epics/epicUtils.js';
import type { Action, AnyAction, Reducer, Store } from 'redux';
import type { IHistoryContext, RouterState } from 'redux-first-history';
import type { EpicMiddleware, Options } from 'redux-observable';
import type { AjaxRequest, AjaxResponse } from 'rxjs/ajax';

// By default, use the same URL and headers, this is the right call browser-side
export const DEFAULT_AJAX_HOOK: (
  url: string,
  headers?: Record<string, string>
) => { url: string; headers?: Record<string, string>; createXHR?: () => XMLHttpRequest } = (url, headers) => {
  return {
    url,
    headers,
  };
};

export interface ItemsQuery {
  limit?: number;
  offset: number;
  search?: string;
  status?: string;
  filterText?: string;
}

export interface ActionWithId extends Action {
  id: number;
  displayId?: string;
}

export interface ActionState<T = unknown> {
  query?: ItemsQuery;
  phase: ActionPhase;
  value: ActionWithId & T;
}

export interface ActionsHistory<T = unknown> {
  actions?: ActionState<T>[];
}

export interface SelfServiceState {
  auxiliaryEsimOrder?: (AuxiliaryEsimOrderState & ActionsHistory) | null;
  billingAccounts?: (BillingAccountsState & ActionsHistory) | null;
  onlineOrders?: (OnlineOrderState & ActionsHistory<SubmitOnlineOrderAction>) | null;
  payments?: PaymentsState | null;
  pendingSubscriptionActions?: SubscriptionActionsState | null;
  punchoutOrders?: PunchoutOrderState | null;
  reports?: ReportsState | null;
  supportCases?: (SupportCasesState & ActionsHistory) | null;
  virtualCatalogs?: (VirtualCatalogsState & ActionsHistory) | null;
}

export interface DeviceCheckoutState {
  billingAccount?: BillingAccount;
  cartItems: Array<ShoppingCartItemForCheckout>;
  cartUpdated: boolean;
  errors?: CommonError[];
  deliveryDetails?: DeviceCheckoutDeliveryDetailsType;
  moveToNextStep?: boolean;
  onlineOrderId?: string;
  selectedBaId?: string;
  duplicateContactCheck?: DuplicateContactCheck;
  deliveryMethods?: DeliveryMethods;
  latestItemAddedToCart?: ShoppingCartItemForCheckout;
  latestItemAddedToCartTimestamp?: number;
}

export interface LayoutState {
  hasChanged: boolean;
  hideHeaderFooter: boolean;
  hideSmallHeader?: boolean;
}

export interface UserState {
  /**
   * @deprecated Use `authenticated` from `AuthProvider` instead, e.g. `useAuth()`.
   * NOTE!
   *   If you need auth stuff, use `AuthProvider`.
   *   If you need non-auth stuff, then continue to use this deprecated redux store (e.g. `useSelector()`).
   **/
  authenticated?: (AuthenticatedUserDataState & ActionsHistory) | null;
}

export interface AuxiliaryEsimOrderState extends ErrorableState {
  errorReason?: AuxiliaryEsimOrderFailureEnum;
  onlineRequestId?: string;
  orderResult?: AuxiliaryEsimServiceOrderResult;
  phase: AuxiliaryEsimOrderPhaseEnum;
  subscription?: Subscription;
  subscriptionToken?: string;
}

export interface State {
  campaigns?: CampaignsState | null;
  deviceCheckout?: DeviceCheckoutState | null;
  lastAction: Action;
  layout?: LayoutState | null;
  payments?: PaymentsState | null;
  pickupPointSearch?: PickupPointSearchState | null;
  resources?: ResourcesState | null;
  router?: RouterState | null;
  selfservice?: SelfServiceState;
  shoppingCart?: ShoppingCartState | null;
  user?: (UserState & ActionsHistory) | null;
}

// Here we can add more initial state, this must be called by all locations that create a store.
// At least some tests override some state, which is why 'initialState' is last.
export const addExtraState = (initialState: State): State => ({
  resources: {},
  user: {
    authenticated: {},
  },
  ...initialState,
});

interface SiteReducers {
  authenticatedUserReducer: (s: AuthenticatedUserDataState, a: Action) => AuthenticatedUserDataState | null;
  auxiliaryEsimOrderReducer: (s: AuxiliaryEsimOrderState, a: Action) => AuxiliaryEsimOrderState | null;
  campaignsReducer: (s: CampaignsState, a: Action) => CampaignsState | null;
  deviceCheckoutReducer: (s: DeviceCheckoutState, a: Action) => DeviceCheckoutState | null;
  layoutReducer: (s: LayoutState, a: Action) => LayoutState | null;
  onlineOrderReducer: (s: OnlineOrderState, a: Action) => OnlineOrderState | null;
  paymentsReducer: (s: PaymentsState, a: Action) => PaymentsState | null;
  pendingSubscriptionActionsReducer: (s: SubscriptionActionsState, a: Action) => SubscriptionActionsState | null;
  pickupPointSearchReducer: (s: PickupPointSearchState, a: Action) => PickupPointSearchState | null;
  punchoutOrderReducer: (s: PunchoutOrderState, a: Action) => PunchoutOrderState | null;
  reportsReducer: (s: ReportsState, a: Action) => ReportsState | null;
  resourcesReducer: (s: ResourcesState, a: Action) => ResourcesState | null;
  shoppingCartReducer: (s: ShoppingCartState, a: Action) => ShoppingCartState | null;
  supportCaseReducer: (s: ActionsHistory, a: Action) => ActionsHistory | null;
  virtualCatalogsReducer: (s: VirtualCatalogsState, a: Action) => VirtualCatalogsState | null;
}

// When a store is created, an @@INIT action is dispatched so that every reducer returns their initial state.
// This effectively populates the initial state tree.
export const lastAction: Action<string> = { type: '@@INIT' };

export const combineSiteReducers = (siteReducers: SiteReducers, iHistoryContext?: IHistoryContext): Reducer<State> => {
  return combineReducers({
    campaigns: siteReducers.campaignsReducer,
    deviceCheckout: siteReducers.deviceCheckoutReducer,
    lastAction: (actionOrUndefined: Action | undefined, action: Action) => action,
    layout: siteReducers.layoutReducer,
    payments: siteReducers.paymentsReducer,
    pickupPointSearch: siteReducers.pickupPointSearchReducer,
    resources: siteReducers.resourcesReducer,
    router: iHistoryContext?.routerReducer as (s: RouterState, a: Action) => RouterState,
    selfservice: combineReducers({
      auxiliaryEsimOrder: siteReducers.auxiliaryEsimOrderReducer,
      onlineOrders: siteReducers.onlineOrderReducer,
      pendingSubscriptionActions: siteReducers.pendingSubscriptionActionsReducer,
      punchoutOrders: siteReducers.punchoutOrderReducer,
      reports: siteReducers.reportsReducer,
      supportCases: siteReducers.supportCaseReducer,
      virtualCatalogs: siteReducers.virtualCatalogsReducer,
    }),
    shoppingCart: siteReducers.shoppingCartReducer,
    user: combineReducers({
      authenticated: siteReducers.authenticatedUserReducer,
    }),
  });
};

const createSiteEpicMiddleware = (
  ajaxHook: (
    url: string,
    headers?: Record<string, string>
  ) => { url: string; headers?: Record<string, string>; createXHR?: () => XMLHttpRequest }
): EpicMiddleware<Action, Action, State, EpicDependencies> => {
  const mapResponse = map((x: AjaxResponse) => x.response);
  const dependencies = {
    get: (url: string, headers?: Record<string, string>) => {
      const request: AjaxRequest = {
        method: 'GET',
        ...ajaxHook(url, headers),
      };
      // this has been fixed in rxjs v7 (https://github.com/ReactiveX/rxjs/issues/2835) but we are still on v6 so this needs to be handled explicitly
      if (headers?.Accept === 'application/xml') {
        request.responseType = 'xml';
      }
      return ajax(request);
    },
    getJSON: (url: string, headers?: Record<string, string>) => {
      return mapResponse(
        ajax({
          method: 'GET',
          ...ajaxHook(url, headers),
        })
      );
    },
    post: (url: string, body?: object, headers?: Record<string, string>) => {
      return ajax({
        method: 'POST',
        body,
        ...ajaxHook(url, headers),
      });
    },
    put: (url: string, body?: object, headers?: Record<string, string>) => {
      return ajax({
        method: 'PUT',
        body,
        ...ajaxHook(url, headers),
      });
    },
    patch: (url: string, body?: object, headers?: Record<string, string>) => {
      return ajax({
        method: 'PATCH',
        body,
        ...ajaxHook(url, headers),
      });
    },
    delete: (url: string, headers?: Record<string, string>) => {
      return ajax({
        method: 'DELETE',
        ...ajaxHook(url, headers),
      });
    },
  };
  const options: Options = { dependencies };
  return createEpicMiddleware(options);
};

// TODO: Remove this when elastic search feature goes live.
const customCombineReducers = (iHistoryContext: IHistoryContext) => {
  return (states: State, actions: AnyAction): State => {
    const latestState = combineSiteReducers(reducers, iHistoryContext)(states, actions);

    return {
      ...latestState,
      selfservice: {
        ...latestState.selfservice,
      },
    };
  };
};

export const configureStoreWithMiddleware = (
  iHistoryContext: IHistoryContext,
  initialState: State
): { store: Store<State>; epicMiddleware: EpicMiddleware<Action, Action, State, EpicDependencies> } => {
  const epicMiddleware = createSiteEpicMiddleware(DEFAULT_AJAX_HOOK);
  const store = configureStore({
    preloadedState: addExtraState(restoreRedirectedState(initialState)),
    reducer: customCombineReducers(iHistoryContext),
    middleware: [
      iHistoryContext.routerMiddleware,
      epicMiddleware,
      notificationBridgeMiddleware,
      dialogBridgeMiddleware,
    ],
    devTools: process.env.NODE_ENV === 'development',
  });
  return {
    store,
    epicMiddleware,
  };
};
